import React from 'react';
import { formatDuration } from '../utils/videoUtils';

/**
 * VideoProgressBar - Animated progress bar for video processing
 * Shows percentage, ETA, and current step during detection/processing
 */
export default function VideoProgressBar({
  progress = 0,
  eta = 0,
  step = '',
  status = 'processing',
  onCancel,
  showCancel = false,
  title = 'Processing Video',
}) {
  const isComplete = status === 'completed';
  const isFailed = status === 'failed';
  const isProcessing = status === 'processing' || status === 'queued';

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          {isProcessing && (
            <svg className="animate-spin h-5 w-5 text-emerald-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {isComplete && (
            <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {isFailed && (
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {title}
        </h3>

        {showCancel && isProcessing && onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="relative mb-3">
        <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ease-out rounded-full ${
              isFailed ? 'bg-red-500' : isComplete ? 'bg-emerald-500' : 'bg-emerald-400'
            }`}
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>

        {/* Animated shimmer overlay for processing state */}
        {isProcessing && progress > 0 && progress < 100 && (
          <div
            className="absolute top-0 left-0 h-full rounded-full overflow-hidden"
            style={{ width: `${progress}%` }}
          >
            <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm">
        {/* Percentage */}
        <span className={`font-medium ${isFailed ? 'text-red-400' : 'text-emerald-400'}`}>
          {progress}%
        </span>

        {/* Current step */}
        <span className="text-gray-400 text-center flex-1 mx-4 truncate">
          {step || (isComplete ? 'Complete!' : isFailed ? 'Failed' : 'Starting...')}
        </span>

        {/* ETA */}
        {isProcessing && eta > 0 && (
          <span className="text-gray-400">
            ETA: {formatDuration(eta)}
          </span>
        )}
        {isComplete && (
          <span className="text-emerald-400">Done</span>
        )}
        {isFailed && (
          <span className="text-red-400">Error</span>
        )}
      </div>

      {/* Detailed stats for longer operations */}
      {isProcessing && progress > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700/50">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-white">{progress}%</p>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatDuration(eta)}</p>
              <p className="text-xs text-gray-500">Remaining</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">
                {status === 'queued' ? 'Queued' : 'Active'}
              </p>
              <p className="text-xs text-gray-500">Status</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add shimmer animation to tailwind via style tag
const shimmerStyle = document.createElement('style');
shimmerStyle.textContent = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
`;
if (!document.head.querySelector('[data-shimmer-style]')) {
  shimmerStyle.setAttribute('data-shimmer-style', 'true');
  document.head.appendChild(shimmerStyle);
}
